/**
 * OpcionesPlato.js — dos piezas que trabajan juntas:
 *
 *  · ProductOptionsPicker : el modal que aparece en Caja al tocar un plato que
 *    tiene opciones ("elige 2 proteínas", "término", "acompañamiento").
 *  · ProductOptionsLinker : el bloque que se inserta en el editor de producto
 *    del Inventario para decir qué grupos usa ESE plato y cuántos exige.
 *
 * Están en el mismo archivo porque comparten el modelo mental y las reglas de
 * mín/máx; separarlos obligaría a mantener esas reglas sincronizadas en dos
 * lugares, que es justo como se desincronizan.
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { X, Check, AlertTriangle } from 'lucide-react';

/* ================================================================== *
 * 1. Selección al vender
 * ================================================================== */

export function ProductOptionsPicker({ product, token, onCancel, onConfirm }) {
  const [groups, setGroups] = useState(null);
  const [picked, setPicked] = useState({}); // { groupId: [itemId] }
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/pos/products/${product.id}/options`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => { if (alive) setGroups(res.data || []); })
      .catch(() => { if (alive) setGroups([]); });
    return () => { alive = false; };
  }, [product.id, token]);

  const toggle = (group, item) => {
    setError('');
    setPicked(prev => {
      const cur = prev[group.id] || [];
      const has = cur.includes(item.id);
      if (has) return { ...prev, [group.id]: cur.filter(x => x !== item.id) };
      // Cuando el máximo es 1 (término de la carne, acompañamiento), elegir otra
      // REEMPLAZA en vez de rebotar con un error: es lo que la mano espera.
      if (group.max_select === 1) return { ...prev, [group.id]: [item.id] };
      if (cur.length >= group.max_select) {
        setError(`Máximo ${group.max_select} en "${group.name}"`);
        return prev;
      }
      return { ...prev, [group.id]: [...cur, item.id] };
    });
  };

  const extra = useMemo(() => {
    if (!groups) return 0;
    let sum = 0;
    for (const g of groups) {
      for (const id of (picked[g.id] || [])) {
        const it = g.items.find(i => i.id === id);
        if (it) sum += Number(it.price_delta) || 0;
      }
    }
    return sum;
  }, [groups, picked]);

  // Un grupo obligatorio cuyas opciones se agotaron todas deja el plato sin
  // poder venderse. Se avisa con claridad en vez de dejar el botón muerto sin
  // explicación — el cajero tiene que saber que hay que ir a Inventario.
  const blocked = useMemo(
    () => (groups || []).filter(g => g.min_select > 0 && g.items.filter(i => i.is_available).length === 0),
    [groups]
  );

  const missing = useMemo(() => {
    if (!groups) return [];
    return groups.filter(g => (picked[g.id] || []).length < g.min_select);
  }, [groups, picked]);

  const confirm = () => {
    if (blocked.length) return;
    if (missing.length) {
      const g = missing[0];
      setError(g.min_select === 1 ? `Elige una opción en "${g.name}"` : `Elige ${g.min_select} en "${g.name}"`);
      return;
    }
    onConfirm({
      selectedOptions: Object.entries(picked)
        .filter(([, ids]) => ids.length)
        .map(([groupId, itemIds]) => ({ groupId: Number(groupId), itemIds })),
      // Resumen legible para la comanda de cocina y el recibo. Se arma acá
      // porque acá están los nombres; el backend guarda su propia copia
      // validada, esto es solo para mostrar al instante en el carrito.
      optionsLabel: (groups || [])
        .map(g => {
          const names = (picked[g.id] || []).map(id => g.items.find(i => i.id === id)?.name).filter(Boolean);
          return names.length ? `${g.name}: ${names.join(', ')}` : null;
        })
        .filter(Boolean)
        .join(' · '),
      optionsPriceDelta: Math.round(extra * 100) / 100
    });
  };

  if (groups === null) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 text-gray-500">Cargando opciones...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl w-full max-w-md max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 border-b">
          <div>
            <h3 className="font-bold text-gray-800">{product.name}</h3>
            <p className="text-sm text-gray-500">
              ${Number(product.price).toFixed(2)}
              {extra > 0 && <span className="text-blue-600 font-semibold"> + ${extra.toFixed(2)} en opciones</span>}
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {blocked.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                No se puede vender: <b>{blocked.map(g => g.name).join(', ')}</b> no tiene ninguna opción
                disponible. Habilita alguna desde Inventario → Opciones.
              </span>
            </div>
          )}

          {groups.map(g => {
            const cur = picked[g.id] || [];
            const available = g.items.filter(i => i.is_available);
            return (
              <div key={g.id}>
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-semibold text-gray-800 text-sm">{g.name}</p>
                  <span className={`text-[11px] font-semibold ${cur.length >= g.min_select ? 'text-green-600' : 'text-amber-600'}`}>
                    {g.min_select === 0
                      ? 'opcional'
                      : g.min_select === g.max_select
                        ? `elige ${g.min_select} · ${cur.length}/${g.min_select}`
                        : `${g.min_select}–${g.max_select} · ${cur.length}`}
                  </span>
                </div>
                {g.description && <p className="text-[11px] text-gray-500">{g.description}</p>}

                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                  {available.map(i => {
                    const on = cur.includes(i.id);
                    return (
                      <button key={i.id} onClick={() => toggle(g, i)}
                        className={`px-2.5 py-2 rounded-lg border text-sm text-left flex items-center gap-1.5 ${on ? 'border-blue-500 bg-blue-50 text-blue-800 font-semibold' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}>
                        {on && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                        <span className="flex-1 truncate">{i.name}</span>
                        {i.price_delta !== 0 && (
                          <span className="text-[11px] text-gray-500">
                            {i.price_delta > 0 ? '+' : ''}{i.price_delta.toFixed(2)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {g.items.length > available.length && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Agotado hoy: {g.items.filter(i => !i.is_available).map(i => i.name).join(', ')}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t space-y-2">
          {error && <p className="text-sm text-red-600 font-semibold">{error}</p>}
          <button onClick={confirm} disabled={blocked.length > 0}
            className="w-full py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
            Agregar al pedido · ${(Number(product.price) + extra).toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== *
 * 2. Vínculo desde el Inventario
 * ================================================================== */

export function ProductOptionsLinker({ productId, token, value, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const H = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API_URL}/api/pos/option-groups`, H),
      productId ? axios.get(`${API_URL}/api/pos/products/${productId}/options`, H) : Promise.resolve({ data: [] })
    ])
      .then(([cat, mine]) => {
        if (!alive) return;
        setCatalog(cat.data || []);
        // Solo se siembra si el padre todavía no tiene nada: al reabrir el modal
        // no debe pisar lo que el usuario ya venía editando.
        if (!value) {
          onChange((mine.data || []).map(g => ({
            group_id: g.id, min_select: g.min_select, max_select: g.max_select
          })));
        }
      })
      .catch(() => { if (alive) setCatalog([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, token]);

  const selected = value || [];
  const byId = new Map(selected.map(s => [s.group_id, s]));

  const toggleGroup = (g) => {
    if (byId.has(g.id)) onChange(selected.filter(s => s.group_id !== g.id));
    else onChange([...selected, { group_id: g.id, min_select: g.min_select, max_select: g.max_select }]);
  };

  const setRange = (groupId, field, raw) => {
    const n = Math.max(0, Math.min(20, Number(raw) || 0));
    onChange(selected.map(s => {
      if (s.group_id !== groupId) return s;
      const next = { ...s, [field]: n };
      // El máximo nunca puede quedar por debajo del mínimo: si pasara, el plato
      // no se podría vender y el error aparecería recién en la caja.
      if (next.max_select < next.min_select) {
        if (field === 'min_select') next.max_select = next.min_select;
        else next.min_select = next.max_select;
      }
      return next;
    }));
  };

  if (loading) return <p className="text-xs text-gray-400">Cargando grupos de opciones...</p>;

  if (catalog.length === 0) {
    return (
      <p className="text-xs text-gray-500 bg-gray-50 border rounded-lg p-3">
        Todavía no hay grupos de opciones. Créalos en <b>Inventario → Opciones</b> (por ejemplo
        "Proteínas" con carne, pollo, chorizo) y después vuelve acá para asignarlos a este plato.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-gray-500">
        Marca los grupos que el cajero debe elegir al vender este plato. El mín/máx de acá pisa
        al del grupo: así el mismo catálogo de proteínas sirve para la menestra simple (1) y la doble (2).
      </p>
      {catalog.filter(g => g.active).map(g => {
        const link = byId.get(g.id);
        return (
          <div key={g.id} className={`border rounded-lg p-2.5 ${link ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={!!link} onChange={() => toggleGroup(g)} className="mt-1" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-gray-800">{g.name}</span>
                <span className="block text-[11px] text-gray-500">
                  {g.items.length} opciones: {g.items.map(i => i.name).join(', ') || '—'}
                </span>
              </span>
            </label>
            {link && (
              <div className="flex gap-2 items-end mt-2 pl-6">
                <label className="text-[11px] text-gray-600">
                  Obligatorias
                  <input type="number" min="0" max="20" value={link.min_select}
                    onChange={e => setRange(g.id, 'min_select', e.target.value)}
                    className="w-16 px-2 py-1 border rounded text-sm block mt-0.5" />
                </label>
                <label className="text-[11px] text-gray-600">
                  Máximo
                  <input type="number" min="1" max="20" value={link.max_select}
                    onChange={e => setRange(g.id, 'max_select', e.target.value)}
                    className="w-16 px-2 py-1 border rounded text-sm block mt-0.5" />
                </label>
                <p className="text-[11px] text-blue-700 flex-1 pb-1">
                  {link.min_select === 0
                    ? 'Opcional'
                    : link.min_select === link.max_select
                      ? `Exactamente ${link.min_select}`
                      : `Entre ${link.min_select} y ${link.max_select}`}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Guarda el vínculo. Se llama después de crear/editar el producto. */
export async function saveProductOptions(productId, groups, token) {
  if (!productId) return;
  await axios.put(`${API_URL}/api/pos/products/${productId}/options`,
    { groups: groups || [] },
    { headers: { Authorization: `Bearer ${token}` } });
}
